// CONTRACT:C4-DESIGN-TOKENS.1.0
/** Theme validation + per-value type checking. A loadable theme MUST define a value for
 *  EVERY semantic slot (completeness) and each value MUST match its declared slot type;
 *  otherwise the whole theme is rejected with ErrInvalidTheme (collecting ErrTokenTypeMismatch
 *  reasons) before it becomes active — the previous theme stays active and the UI never
 *  renders half-themed. */

import type { Theme } from "../../contracts-ts/src/index.ts";
import { SLOTS, SLOT_TYPES } from "./slots.ts";
import type { SlotDef } from "./slots.ts";
import { ErrInvalidTheme, ErrTokenTypeMismatch } from "./errors.ts";
import type { SlotType } from "./errors.ts";

const COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_RE = /^(?:rgb|rgba|hsl|hsla)\(/;
const LENGTH_RE = /^(?:0|-?\d*\.?\d+(?:px|rem|em|%|vh|vw))$/;
const DURATION_RE = /^\d*\.?\d+m?s$/;

/** Does `value` (already known to be a string) satisfy the declared slot `type`? */
export function valueMatchesType(value: string, type: SlotType): boolean {
  switch (type) {
    case "color":
      return COLOR_RE.test(value) || RGB_RE.test(value) || value === "transparent";
    case "length":
      // Elevation uses box-shadow strings ("none" / "0 1px 3px ...") which are length-class.
      return value.length > 0;
    case "fontSize":
      return LENGTH_RE.test(value);
    case "fontFamily":
      return value.length > 0;
    case "duration":
      return DURATION_RE.test(value);
    case "easing":
      return value.length > 0;
  }
}

/** Throw ErrTokenTypeMismatch if a single token value is the wrong runtime type or fails
 *  its declared-type predicate. `received` is unknown so non-string inputs (e.g. a number
 *  where a color is required, per Scenario 2) are caught. */
export function assertTokenType(key: string, received: unknown): void {
  const type = SLOT_TYPES[key];
  // Unknown slot keys are a completeness concern, not a type concern; ignore here.
  if (!type) return;
  if (typeof received !== "string") {
    throw new ErrTokenTypeMismatch(key, type, received);
  }
  if (!valueMatchesType(received, type)) {
    throw new ErrTokenTypeMismatch(key, type, received);
  }
}

/** Result of a non-throwing validation pass. */
export interface ThemeValidation {
  readonly valid: boolean;
  /** Per-slot reasons the theme would be rejected (empty when valid). */
  readonly reasons: readonly string[];
}

/** Validate a theme WITHOUT throwing — returns completeness + type-mismatch reasons.
 *  Treats the theme's `tokens` as untrusted (e.g. parsed JSON from a coalition skin). */
export function validateTheme(theme: Theme): ThemeValidation {
  const reasons: string[] = [];
  const tokens = (theme.tokens ?? {}) as Record<string, unknown>;

  for (const slot of SLOTS as readonly SlotDef[]) {
    if (!(slot.key in tokens)) {
      reasons.push(`missing slot "${slot.key}"`);
      continue;
    }
    const v = tokens[slot.key];
    if (typeof v !== "string") {
      reasons.push(
        `slot "${slot.key}" expected ${slot.type} but got ${typeof v}`,
      );
      continue;
    }
    if (!valueMatchesType(v, slot.type)) {
      reasons.push(
        `slot "${slot.key}" value ${JSON.stringify(v)} is not a valid ${slot.type}`,
      );
    }
  }

  return { valid: reasons.length === 0, reasons };
}

/** Validate and throw ErrInvalidTheme on any failure. Use before activating a theme so an
 *  incomplete/malformed set never becomes active. Returns the theme unchanged when valid. */
export function assertValidTheme(theme: Theme): Theme {
  const { valid, reasons } = validateTheme(theme);
  if (!valid) {
    throw new ErrInvalidTheme(theme?.id ?? "<unknown>", reasons);
  }
  return theme;
}
